import {
  IonContent,
  IonLoading,
  IonModal,
  IonSelect,
  IonSelectOption
} from '@ionic/react';
import React, { useEffect } from 'react';

import WeeklyReportPatients from './weeklyReportPatients/WeeklyReportPatients';
import { DataItems, ModalHeader, Padding, Spacer } from '@/components/common';
import { Text } from '@/components/memo';
import { useDentist, useWeeklyReports } from '@/contexts';
import { useDimensions, useToast } from '@/hooks';
import {
  WEEKLY_REPORT_DEFAULT_CHART_HEIGHT,
  WEEKLY_REPORT_DEFAULT_CHART_MAX_WIDTH,
  WEEKLY_REPORT_DEFAULT_CHART_WIDTH,
  WEEKLY_REPORT_DEFAULT_DENTIST,
  WEEKLY_REPORT_SUBSCRIPTION_TYPES
} from '@/utils/constants';
import { isClinicName } from '@/utils/helpers/isClinicName';
import { parseDate } from '@/utils/helpers/parseDate';
import { addDays, format, getMonth } from 'date-fns';
import {
  arrowBackOutline,
  calendarOutline,
  eyeOffOutline,
  timerOutline
} from 'ionicons/icons';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis } from 'recharts';

import './WeeklyReports.css';

interface WeeklyReportsProps {
  isOpen: boolean;
  onClose: () => void;
  presentingElement?: HTMLElement | undefined;
}

const WeeklyReports: React.FC<WeeklyReportsProps> = ({
  isOpen,
  onClose,
  presentingElement
}) => {
  const displayToast = useToast();
  const { selectedDentist: dentist } = useDentist();
  const {
    latestStartDate,
    weeklyReportData,
    startDates,
    loadingStartDates,
    loadedStartDates,
    fetchStartDates,
    dentists,
    loading,
    selectedStartDate,
    setSelectedStartDate,
    selectedDentist,
    setSelectedDentist,
    chartData,
    totalAppointments,
    noShowRate,
    lateRate,
    noShowPatients,
    latePatients
  } = useWeeklyReports();
  const { width } = useDimensions();

  const isClinicReport =
    weeklyReportData?.reportType === WEEKLY_REPORT_SUBSCRIPTION_TYPES.CLINIC;

  const reportDataItems = [
    {
      icon: calendarOutline,
      title: 'Total Appointments',
      value: totalAppointments
    },
    {
      icon: eyeOffOutline,
      title: 'No-show',
      value: `${noShowRate}%`
    },
    {
      icon: timerOutline,
      title: 'Late',
      value: `${lateRate}%`
    }
  ];

  const copy = {
    noShowPatients: (
      <>
        <Text color="primary">No-show</Text> Patients
      </>
    ),
    latePatients: (
      <>
        <Text color="primary">Late</Text> Patients
      </>
    )
  };

  const formatWeek = (startDate: string) => {
    const startOfWeek = parseDate(startDate);
    const endOfWeek = addDays(startOfWeek, 6);

    const isSameMonth = getMonth(startOfWeek) === getMonth(endOfWeek);

    return `${format(startOfWeek, 'MMMM d')} - ${format(
      endOfWeek,
      isSameMonth ? 'd, yyyy' : 'MMMM d, yyyy'
    )}`;
  };

  const renderBarLabel = ({ x, y, width, height, value }: any) => {
    if (
      value === Math.max(...chartData.map((data) => data.Appointments)) &&
      value !== 0
    ) {
      return (
        <text
          x={x + width / 2}
          y={y + height / 2}
          fill="#cbc9ff"
          textAnchor="middle"
        >
          {value}
        </text>
      );
    }

    return <></>;
  };

  useEffect(() => {
    if (isOpen) {
      fetchStartDates();
    }
  }, [isOpen, fetchStartDates]);

  useEffect(() => {
    if (isOpen && latestStartDate) {
      setSelectedStartDate(latestStartDate);
    } else {
      setSelectedStartDate(null);
    }
  }, [isOpen, latestStartDate]);

  useEffect(() => {
    if (!dentist?.id) {
      onClose();
      return;
    }

    if (startDates.length === 0 && !loadingStartDates && loadedStartDates) {
      onClose();
      displayToast({
        message:
          'You currently have no weekly reports available. Please check back next week for updates.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  }, [startDates, loadingStartDates, loadedStartDates, dentist?.id]);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      presentingElement={presentingElement}
    >
      <IonContent>
        <Padding withSafeArea={!presentingElement}>
          <div className="weekly-reports">
            <ModalHeader
              leftIcon={arrowBackOutline}
              onClickLeftIcon={onClose}
            />
            <Spacer top={16} />
            <div id="weekly-reports-selects">
              <IonSelect
                placeholder="Select Week"
                onIonChange={(e) => setSelectedStartDate(e.detail.value)}
                value={selectedStartDate}
                label="Week"
                color="dark"
              >
                {startDates.map((date) => (
                  <IonSelectOption key={date} value={date}>
                    {formatWeek(date)}
                  </IonSelectOption>
                ))}
              </IonSelect>
              {isClinicReport && (
                <IonSelect
                  placeholder="Select Dentist"
                  onIonChange={(e) => setSelectedDentist(e.detail.value)}
                  value={selectedDentist}
                  label="Dentist"
                  color="dark"
                >
                  {dentists.map((dentist) => (
                    <IonSelectOption key={dentist} value={dentist}>
                      {dentist === WEEKLY_REPORT_DEFAULT_DENTIST
                        ? dentist
                        : `${isClinicName(dentist) ? '' : 'Dr. '}${dentist}`}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              )}
            </div>
            <Spacer top={24}>
              <div className="weekly-reports__chart">
                {chartData && (
                  <BarChart
                    width={
                      Math.min(
                        width ?? WEEKLY_REPORT_DEFAULT_CHART_WIDTH,
                        WEEKLY_REPORT_DEFAULT_CHART_MAX_WIDTH
                      ) - 32
                    }
                    height={WEEKLY_REPORT_DEFAULT_CHART_HEIGHT}
                    data={chartData}
                    id="weekly-reports-chart"
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="Day"
                      tickFormatter={(value) => value.slice(0, 1)}
                      tickLine={false}
                    />
                    <Tooltip />
                    <Bar
                      dataKey="Appointments"
                      fill="#534292"
                      label={renderBarLabel}
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                )}
              </div>
            </Spacer>
            <Spacer
              top={24}
              bottom={
                noShowPatients.length > 0 || latePatients.length > 0 ? 0 : 42
              }
            >
              <div id="weekly-reports-data">
                {reportDataItems.length > 0 && (
                  <DataItems items={reportDataItems} />
                )}
              </div>
            </Spacer>
            {noShowPatients.length > 0 && (
              <Spacer top={42} bottom={latePatients.length > 0 ? 0 : 42}>
                <WeeklyReportPatients
                  header={copy.noShowPatients}
                  patients={noShowPatients}
                />
              </Spacer>
            )}
            {latePatients.length > 0 && (
              <Spacer top={42} bottom={42}>
                <WeeklyReportPatients
                  header={copy.latePatients}
                  patients={latePatients}
                />
              </Spacer>
            )}
          </div>
          <IonLoading
            isOpen={loading || loadingStartDates}
            spinner="crescent"
            message="Loading..."
          />
        </Padding>
      </IonContent>
    </IonModal>
  );
};

export default WeeklyReports;
